import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Loader2,
  Send,
  Search,
  X,
  Users,
  Building2,
  User,
  Info,
  AlertTriangle,
  ShieldAlert,
  Zap,
  Bell,
} from "lucide-react";
import { toast } from "sonner";
import { getCompanies } from "@/services/superAdminService";
import {
  listBroadcastUsers,
  previewBroadcast,
  sendBroadcast,
} from "@/services/broadcastService";
import { useDebounce } from "@/utils/useDebounce";

const TYPE_META = {
  info: {
    label: "Info",
    Icon: Info,
    tint: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
    ring: "border-blue-500 ring-2 ring-blue-500/25",
    accent: "border-l-blue-500",
  },
  warning: {
    label: "Warning",
    Icon: AlertTriangle,
    tint: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
    ring: "border-amber-500 ring-2 ring-amber-500/25",
    accent: "border-l-amber-500",
  },
  critical: {
    label: "Critical",
    Icon: ShieldAlert,
    tint: "bg-red-500/10 text-red-600 dark:text-red-400",
    ring: "border-red-500 ring-2 ring-red-500/25",
    accent: "border-l-red-500",
  },
  action: {
    label: "Action",
    Icon: Zap,
    tint: "bg-violet-500/10 text-violet-600 dark:text-violet-400",
    ring: "border-violet-500 ring-2 ring-violet-500/25",
    accent: "border-l-violet-500",
  },
};
const TYPE_ORDER = ["info", "warning", "critical", "action"];

const AUDIENCE_TABS = [
  { value: "all", label: "All", Icon: Users },
  { value: "company", label: "Company", Icon: Building2 },
  { value: "users", label: "Users", Icon: User },
];

const ROLES = [
  { value: "all", label: "Everyone (admins + employees)" },
  { value: "admin", label: "Admins only" },
  { value: "employee", label: "Employees only" },
];
const roleNoun = { all: "users", admin: "admins", employee: "employees" };

const SendNotification = () => {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [type, setType] = useState("info");
  const [audience, setAudience] = useState("all");
  const [role, setRole] = useState("all");
  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [companyFilter, setCompanyFilter] = useState("");
  const [selectedCompanyIds, setSelectedCompanyIds] = useState([]);

  const [userSearch, setUserSearch] = useState("");
  const debouncedUserSearch = useDebounce(userSearch, 400);
  const [userResults, setUserResults] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);

  const [reach, setReach] = useState(null); // recipient count
  const [countingReach, setCountingReach] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await getCompanies({ limit: 500 });
        setCompanies(res.companies || []);
      } catch {
        /* non-fatal */
      }
    })();
  }, []);

  // ---- audience payload (null when the current audience is incomplete) ----
  const audiencePayload = useMemo(() => {
    if (audience === "all") return { audience: "all", role };
    if (audience === "company")
      return selectedCompanyIds.length
        ? { audience: "company", company_ids: selectedCompanyIds, role }
        : null;
    return selectedUsers.length
      ? { audience: "users", user_ids: selectedUsers.map((u) => u._id) }
      : null;
  }, [audience, role, selectedCompanyIds, selectedUsers]);

  // ---- live recipient count ----
  const reachKey = JSON.stringify(audiencePayload);
  const debouncedReachKey = useDebounce(reachKey, 350);
  useEffect(() => {
    const payload = debouncedReachKey ? JSON.parse(debouncedReachKey) : null;
    if (!payload) {
      setReach(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setCountingReach(true);
        const res = await previewBroadcast(payload);
        if (!cancelled) setReach(res?.recipients ?? 0);
      } catch {
        if (!cancelled) setReach(null);
      } finally {
        if (!cancelled) setCountingReach(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debouncedReachKey]);

  // ---- user search ----
  const searchUsers = useCallback(async () => {
    try {
      setLoadingUsers(true);
      // No search term -> load the full list; a term filters it.
      const res = await listBroadcastUsers(
        debouncedUserSearch ? { search: debouncedUserSearch } : {}
      );
      setUserResults(res || []);
    } catch {
      toast.error("Failed to load users");
    } finally {
      setLoadingUsers(false);
    }
  }, [debouncedUserSearch]);

  useEffect(() => {
    if (audience === "users") searchUsers();
  }, [audience, searchUsers]);

  const toggleCompany = (id) =>
    setSelectedCompanyIds((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  const toggleUser = (u) =>
    setSelectedUsers((prev) =>
      prev.some((s) => s._id === u._id)
        ? prev.filter((s) => s._id !== u._id)
        : [...prev, u]
    );

  const filteredCompanies = companies.filter((c) =>
    (c.name || "").toLowerCase().includes(companyFilter.toLowerCase())
  );

  const audienceSummary = useMemo(() => {
    if (audience === "all") return `All ${roleNoun[role]}`;
    if (audience === "company")
      return `${roleNoun[role][0].toUpperCase()}${roleNoun[role].slice(1)} in ${
        selectedCompanyIds.length
      } compan${selectedCompanyIds.length === 1 ? "y" : "ies"}`;
    return `${selectedUsers.length} selected user${
      selectedUsers.length === 1 ? "" : "s"
    }`;
  }, [audience, role, selectedCompanyIds, selectedUsers]);

  const validate = () => {
    if (!title.trim()) {
      toast.error("Enter a title");
      return false;
    }
    if (!message.trim()) {
      toast.error("Enter a message");
      return false;
    }
    if (!audiencePayload) {
      toast.error(
        audience === "company"
          ? "Select at least one company"
          : "Select at least one user"
      );
      return false;
    }
    return true;
  };

  const handleReview = () => {
    if (validate()) setConfirmOpen(true);
  };

  const handleSend = async () => {
    try {
      setSending(true);
      const res = await sendBroadcast({ title, message, type, ...audiencePayload });
      toast.success(`Notification sent to ${res?.recipients ?? 0} user(s)`);
      setConfirmOpen(false);
      setTitle("");
      setMessage("");
      setSelectedUsers([]);
      setSelectedCompanyIds([]);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to send notification");
    } finally {
      setSending(false);
    }
  };

  const meta = TYPE_META[type];
  const TypeIcon = meta.Icon;

  const ReachPill = (
    <div className="flex items-center gap-2 text-sm">
      <Users className="h-4 w-4 text-muted-foreground" />
      {countingReach ? (
        <span className="text-muted-foreground">Counting…</span>
      ) : reach == null ? (
        <span className="text-muted-foreground">
          Choose an audience to see reach
        </span>
      ) : (
        <span>
          Reaches{" "}
          <span className="font-semibold text-foreground">{reach}</span> user
          {reach === 1 ? "" : "s"}
        </span>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Send Notification</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Broadcast an announcement to selected or all users.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
        {/* ---------------- Left: compose + audience ---------------- */}
        <div className="space-y-6">
          {/* Message */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Message</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label>Title</Label>
                  <span className="text-xs text-muted-foreground">
                    {title.length}/120
                  </span>
                </div>
                <Input
                  value={title}
                  maxLength={120}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Scheduled maintenance tonight"
                />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label>Message</Label>
                  <span className="text-xs text-muted-foreground">
                    {message.length}/1000
                  </span>
                </div>
                <Textarea
                  value={message}
                  rows={4}
                  maxLength={1000}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="What do you want to tell your users?"
                />
              </div>

              <div className="space-y-1.5">
                <Label>Type</Label>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {TYPE_ORDER.map((key) => {
                    const m = TYPE_META[key];
                    const Icon = m.Icon;
                    const active = type === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setType(key)}
                        className={`flex flex-col items-center gap-1.5 rounded-lg border p-3 text-xs font-medium transition-all ${
                          active
                            ? m.ring
                            : "border-border hover:bg-muted/60"
                        }`}
                      >
                        <span
                          className={`flex h-8 w-8 items-center justify-center rounded-full ${m.tint}`}
                        >
                          <Icon className="h-4 w-4" />
                        </span>
                        {m.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Audience */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Audience</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs value={audience} onValueChange={setAudience}>
                <TabsList className="flex justify-start gap-6 bg-transparent h-auto p-0">
                  {AUDIENCE_TABS.map(({ value, label, Icon }) => (
                    <TabsTrigger key={value} value={value}>
                      <Icon className="mr-1.5 h-4 w-4" />
                      {label}
                    </TabsTrigger>
                  ))}
                </TabsList>

                <TabsContent value="all" className="mt-4 space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Sends to every active user across all companies.
                  </p>
                  <div className="space-y-1.5 sm:max-w-xs">
                    <Label>Role filter</Label>
                    <Select value={role} onValueChange={setRole}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ROLES.map((r) => (
                          <SelectItem key={r.value} value={r.value}>
                            {r.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </TabsContent>

                <TabsContent value="company" className="mt-4 space-y-3">
                  <div className="space-y-1.5 sm:max-w-xs">
                    <Label>Role filter</Label>
                    <Select value={role} onValueChange={setRole}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ROLES.map((r) => (
                          <SelectItem key={r.value} value={r.value}>
                            {r.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      className="pl-9"
                      placeholder="Filter companies…"
                      value={companyFilter}
                      onChange={(e) => setCompanyFilter(e.target.value)}
                    />
                  </div>
                  <div className="max-h-64 space-y-0.5 overflow-auto rounded-md border border-border p-2">
                    {filteredCompanies.length === 0 && (
                      <p className="p-2 text-sm text-muted-foreground">
                        No companies found.
                      </p>
                    )}
                    {filteredCompanies.map((c) => (
                      <label
                        key={c._id}
                        className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-muted"
                      >
                        <Checkbox
                          checked={selectedCompanyIds.includes(c._id)}
                          onCheckedChange={() => toggleCompany(c._id)}
                        />
                        <span>{c.name}</span>
                      </label>
                    ))}
                  </div>
                  {selectedCompanyIds.length > 0 && (
                    <p className="text-xs text-muted-foreground">
                      {selectedCompanyIds.length} selected
                    </p>
                  )}
                </TabsContent>

                <TabsContent value="users" className="mt-4 space-y-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      className="pl-9"
                      placeholder="Search users by name or email…"
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                    />
                  </div>

                  {selectedUsers.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {selectedUsers.map((u) => (
                        <Badge
                          key={u._id}
                          variant="secondary"
                          className="flex items-center gap-1"
                        >
                          {u.name || u.email}
                          <button
                            type="button"
                            onClick={() => toggleUser(u)}
                            aria-label="Remove"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}

                  <div className="max-h-64 space-y-0.5 overflow-auto rounded-md border border-border p-2">
                    {loadingUsers ? (
                      <div className="flex justify-center py-4">
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      </div>
                    ) : userResults.length === 0 ? (
                      <p className="p-2 text-sm text-muted-foreground">
                        No users found.
                      </p>
                    ) : (
                      userResults.map((u) => (
                        <label
                          key={u._id}
                          className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-muted"
                        >
                          <Checkbox
                            checked={selectedUsers.some((s) => s._id === u._id)}
                            onCheckedChange={() => toggleUser(u)}
                          />
                          <span className="flex flex-col">
                            <span className="font-medium">
                              {u.name || u.email}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {u.email} · {u.role}
                              {u.company_name ? ` · ${u.company_name}` : ""}
                            </span>
                          </span>
                        </label>
                      ))
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>

        {/* ---------------- Right: sticky preview + send ---------------- */}
        <div className="lg:sticky lg:top-6 lg:h-fit space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-muted-foreground">
                Preview
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div
                className={`rounded-lg border border-l-4 bg-card p-3 ${meta.accent}`}
              >
                <div className="flex gap-3">
                  <span
                    className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${meta.tint}`}
                  >
                    <TypeIcon className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="break-words font-semibold text-foreground">
                      {title || "Notification title"}
                    </p>
                    <p className="mt-0.5 break-words text-sm text-muted-foreground">
                      {message || "Your message will appear here."}
                    </p>
                    <p className="mt-1.5 text-xs text-muted-foreground">
                      Just now
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2 rounded-lg bg-muted/40 p-3">
                <div className="flex items-center gap-2 text-sm">
                  <Bell className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Audience:</span>
                  <span className="font-medium text-foreground">
                    {audienceSummary}
                  </span>
                </div>
                {ReachPill}
              </div>

              <Button className="w-full" onClick={handleReview}>
                <Send className="mr-2 h-4 w-4" />
                Review &amp; send
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Confirm dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send this notification?</DialogTitle>
            <DialogDescription>
              This will deliver the notification immediately and cannot be
              undone.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-1">
            <div
              className={`rounded-lg border border-l-4 bg-card p-3 ${meta.accent}`}
            >
              <div className="flex gap-3">
                <span
                  className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${meta.tint}`}
                >
                  <TypeIcon className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <p className="break-words font-semibold text-foreground">
                    {title}
                  </p>
                  <p className="mt-0.5 break-words text-sm text-muted-foreground">
                    {message}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-muted/40 px-3 py-2 text-sm">
              <span className="text-muted-foreground">{audienceSummary}</span>
              <span className="font-semibold text-foreground">
                {countingReach || reach == null
                  ? "…"
                  : `${reach} recipient${reach === 1 ? "" : "s"}`}
              </span>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={sending}
            >
              Cancel
            </Button>
            <Button onClick={handleSend} disabled={sending}>
              {sending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              Send now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SendNotification;
